import { Link, NavLink, Navigate, Outlet, useNavigate, useParams } from 'react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { logout } from '../../api/auth';
import { ImpersonationBanner } from '../admin/ImpersonationBanner';
import { useMe } from '../auth/useMe';
import { ErrorNotice } from '../../components/ErrorNotice';
import { LoadingScreen } from '../../components/LoadingScreen';

/**
 * `/w/:slug` — authenticated workspace shell (ARCHITECTURE_PLAN §10). Guard
 * chain: `RequireAuth` (route table) guarantees a signed-in user; this shell
 * then verifies MEMBERSHIP for the `:slug` in the path from the
 * `GET /api/auth/me` bootstrap:
 *   - not a member → a real forbidden state naming the workspaces the user
 *     DOES belong to (the server independently 403s; this is the UI surface);
 *   - member       → the shell (header nav + `<Outlet />`).
 *
 * The nav deliberately contains only routes that exist in Phase 1 — inbox,
 * leads, calls, etc. land in later phases (§14) and are NOT stubbed here.
 */
export function WorkspaceShell(): React.JSX.Element {
  const { slug } = useParams<'slug'>();
  const me = useMe();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const logoutMutation = useMutation({
    mutationFn: logout,
    onSuccess: () => {
      // Drop all cached server state, then land on the logged-out surface.
      queryClient.clear();
      void navigate('/login', { replace: true });
    },
  });

  if (slug === undefined) {
    // The route table always provides :slug; this only satisfies the type.
    return <Navigate to="/" replace />;
  }

  if (me.isPending || me.data === undefined) {
    return <LoadingScreen />;
  }

  const membership = me.data.memberships.find((entry) => entry.slug === slug);

  // §6.4: an impersonating platform admin has NO membership in the impersonated
  // tenant (their session is scoped by the grant, not a membership row). The
  // server's workspace middleware resolves them into the tenant with
  // admin-MINUS-billing capabilities; the shell must NOT block that path with
  // a "no access" state. The membership shape is synthesized from the
  // impersonation bootstrap so the shell renders normally.
  const impersonating = me.data.session.isImpersonating;
  const effectiveMembership =
    impersonating && membership === undefined && me.data.session.impersonatingTenantSlug === slug
      ? {
          tenantId: me.data.session.impersonatingTenantId ?? '',
          slug,
          name: slug,
          role: 'admin' as const,
        }
      : membership;

  if (effectiveMembership === undefined) {
    return (
      <main className="flex min-h-screen items-center justify-center px-6">
        <div className="w-full max-w-md text-center">
          <h1 className="text-2xl font-semibold tracking-tight">No access to this workspace</h1>
          <p className="mt-2 text-slate-600">
            You are signed in as <span className="font-medium">{me.data.user?.email}</span>, but
            this account is not a member of <code className="font-mono text-sm">/w/{slug}</code>.
          </p>
          {me.data.memberships.length > 0 && (
            <div className="mt-6">
              <h2 className="text-sm font-medium text-slate-700">Your workspaces</h2>
              <ul className="mt-2 space-y-1">
                {me.data.memberships.map((entry) => (
                  <li key={entry.tenantId}>
                    <Link
                      to={`/w/${entry.slug}`}
                      className="text-sm font-medium text-slate-900 underline underline-offset-4"
                    >
                      {entry.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <p className="mt-6 text-sm text-slate-600">
            Ask a workspace admin for an invite, or{' '}
            <Link
              to="/onboarding"
              className="font-medium text-slate-900 underline underline-offset-4"
            >
              create your own workspace
            </Link>
            .
          </p>
        </div>
      </main>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <ImpersonationBanner />
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-4 px-6 py-3">
          <div className="flex items-center gap-6">
            <span className="text-sm font-semibold tracking-tight">Astralis FrontDesk</span>
            <nav aria-label="Workspace" className="flex items-center gap-4">
              <NavLink
                to={`/w/${slug}`}
                end
                className={({ isActive }) =>
                  `text-sm font-medium ${isActive ? 'text-slate-900' : 'text-slate-500 hover:text-slate-900'}`
                }
              >
                Overview
              </NavLink>
              <NavLink
                to={`/w/${slug}/inbox`}
                className={({ isActive }) =>
                  `text-sm font-medium ${isActive ? 'text-slate-900' : 'text-slate-500 hover:text-slate-900'}`
                }
              >
                Inbox
              </NavLink>
              <NavLink
                to={`/w/${slug}/leads`}
                className={({ isActive }) =>
                  `text-sm font-medium ${isActive ? 'text-slate-900' : 'text-slate-500 hover:text-slate-900'}`
                }
              >
                Leads
              </NavLink>
              <NavLink
                to={`/w/${slug}/calls`}
                className={({ isActive }) =>
                  `text-sm font-medium ${isActive ? 'text-slate-900' : 'text-slate-500 hover:text-slate-900'}`
                }
              >
                Calls
              </NavLink>
              <NavLink
                to={`/w/${slug}/appointments`}
                className={({ isActive }) =>
                  `text-sm font-medium ${isActive ? 'text-slate-900' : 'text-slate-500 hover:text-slate-900'}`
                }
              >
                Appointments
              </NavLink>
              <NavLink
                to={`/w/${slug}/jobs`}
                className={({ isActive }) =>
                  `text-sm font-medium ${isActive ? 'text-slate-900' : 'text-slate-500 hover:text-slate-900'}`
                }
              >
                Jobs
              </NavLink>
              <NavLink
                to={`/w/${slug}/settings/availability`}
                className={({ isActive }) =>
                  `text-sm font-medium ${isActive ? 'text-slate-900' : 'text-slate-500 hover:text-slate-900'}`
                }
              >
                Settings
              </NavLink>
              <NavLink
                to={`/w/${slug}/settings/billing`}
                className={({ isActive }) =>
                  `text-sm font-medium ${isActive ? 'text-slate-900' : 'text-slate-500 hover:text-slate-900'}`
                }
              >
                Billing
              </NavLink>
            </nav>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-slate-600">{me.data.user?.email}</span>
            <button
              type="button"
              onClick={() => logoutMutation.mutate()}
              disabled={logoutMutation.isPending}
              className="rounded-md border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {logoutMutation.isPending ? 'Signing out…' : 'Sign out'}
            </button>
          </div>
        </div>
        {logoutMutation.isError && (
          <div className="mx-auto max-w-5xl px-6 pb-3">
            <ErrorNotice error={logoutMutation.error} />
          </div>
        )}
      </header>

      <main className="mx-auto max-w-5xl px-6 py-8">
        <Outlet context={{ membership: effectiveMembership }} />
      </main>
    </div>
  );
}
